import React from 'react';
import { FileText, Printer } from 'lucide-react';
import { CONTACT_EMAILS } from '../constants/contact';

/**
 * TermsOfService - Terms of Service page for legal compliance (Issue #1310)
 *
 * Provides:
 * - Platform usage terms
 * - User responsibilities
 * - Liability disclaimers
 * - Account termination policies
 */
export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-secondary" />
              <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
            </div>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-secondary hover:text-primary-dark hover:bg-secondary-light/50 rounded-lg transition-colors print:hidden"
              aria-label="Print this document"
            >
              <Printer className="w-5 h-5" />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-2">
            © {new Date().getFullYear()} Local Artist Fairs. All rights reserved.
          </p>
          <p className="text-sm text-gray-600 mb-8">
            Last Updated: March 1, 2026
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to Local Artist Fairs. By accessing or using our platform, you agree to be bound by these Terms of Service
              ("Terms"). If you do not agree to these Terms, please do not use our platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              These Terms apply to all users, including vendors, fair managers, administrators, and visitors.
            </p>
          </section>

          {/* Definitions */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Definitions</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>"Platform"</strong> refers to the Local Artist Fairs website and services</li>
              <li><strong>"User," "You," "Your"</strong> refers to individuals using the platform</li>
              <li><strong>"Vendor"</strong> refers to artists or businesses selling at fairs</li>
              <li><strong>"Fair Manager"</strong> refers to organizers managing fair events</li>
              <li><strong>"Content"</strong> refers to text, images, data, and other materials on the platform</li>
            </ul>
          </section>

          {/* User Accounts */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Accounts</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Creation</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              To use certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be at least 18 years old to create an account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Termination</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to suspend or terminate your account if you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Violate these Terms</li>
              <li>Provide false or misleading information</li>
              <li>Engage in fraudulent activity</li>
              <li>Harm other users or the platform</li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When using the platform, you agree NOT to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Impersonate others or misrepresent your identity</li>
              <li>Scrape, crawl, or data mine the platform</li>
              <li>Interfere with platform functionality or security</li>
              <li>Use the platform for unauthorized commercial purposes</li>
            </ul>
          </section>

          {/* Vendor Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Vendor-Specific Terms</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Booth Reservations</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li>Booth reservations are subject to fair manager approval</li>
              <li>Payment terms and cancellation policies are set by fair managers</li>
              <li>Vendors are responsible for booth setup and compliance with fair rules</li>
              <li>We are not responsible for disputes between vendors and fair managers</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Waitlists and Slot Offers</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Waitlist positions are not guaranteed reservations</li>
              <li>Slot offers expire after the specified time period</li>
              <li>Declining or missing slot offers may affect future waitlist priority</li>
            </ul>
          </section>

          {/* Fair Manager Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Fair Manager-Specific Terms</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Fair managers are responsible for managing fair events and vendor relationships</li>
              <li>Fair managers must comply with all applicable laws and regulations</li>
              <li>Fair managers are responsible for setting accurate pricing and availability</li>
              <li>We are not party to agreements between fair managers and vendors</li>
            </ul>
          </section>

          {/* Payments and Fees */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Payments and Fees</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Payment Processing</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              All payments are processed securely through Stripe. By making a payment, you agree to Stripe's{' '}
              <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                Terms of Service
              </a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Refunds and Cancellations</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Refund and cancellation policies are set by fair managers. Please review the specific fair's policies
              before making a reservation.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Platform Fees</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may charge platform fees for certain services. All fees will be clearly disclosed before you complete a transaction.
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Platform Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The platform and its content (excluding user-generated content) are owned by Local Artist Fairs and protected
              by copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">User Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              By uploading content to the platform, you grant us a non-exclusive, worldwide, royalty-free license to use,
              display, and distribute your content for the purpose of providing and promoting the platform.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              You retain ownership of your content and are responsible for ensuring you have the rights to upload it.
            </p>
          </section>

          {/* Disclaimers */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimers</h2>

            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 mb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">"AS IS" Disclaimer</h3>
              <p className="text-gray-700 leading-relaxed">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Platform Availability</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not guarantee that the platform will be available at all times. We may experience downtime for maintenance,
              updates, or technical issues.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Third-Party Services</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The platform integrates with third-party services (Google Analytics, Sentry, Stripe). We are not responsible
              for the availability, accuracy, or functionality of these services.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>

            <div className="p-4 bg-red-50 border-l-4 border-red-500 mb-4">
              <p className="text-gray-700 leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOCAL ARTIST FAIRS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR
                OTHER INTANGIBLE LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Your use or inability to use the platform</li>
                <li>Unauthorized access to your account or data</li>
                <li>Disputes between vendors and fair managers</li>
                <li>Errors, bugs, or interruptions in the platform</li>
                <li>Third-party content or conduct</li>
              </ul>
            </div>

            <p className="text-gray-700 leading-relaxed">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE LAST 12 MONTHS, OR $100, WHICHEVER IS GREATER.
            </p>
          </section>

          {/* Indemnification */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Indemnification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless Local Artist Fairs and its officers, directors, employees,
              and agents from any claims, liabilities, damages, losses, or expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Your use of the platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Content you upload to the platform</li>
            </ul>
          </section>

          {/* Dispute Resolution */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Dispute Resolution</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Informal Resolution</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have a dispute with us, please contact us first to attempt an informal resolution.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Governing Law</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms are governed by the laws of the State of Rhode Island, USA, without regard to conflict of law principles.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Arbitration</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any disputes that cannot be resolved informally shall be settled through binding arbitration in accordance
              with the American Arbitration Association's rules.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by posting
              the new Terms on this page and updating the "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of the platform after changes are posted constitutes your acceptance of the new Terms.
            </p>
          </section>

          {/* Contact Us */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">
                <strong>Email:</strong>{' '}
                <a href={`mailto:${CONTACT_EMAILS.legal}`} className="text-secondary hover:text-primary-dark underline">
                  {CONTACT_EMAILS.legal}
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Feedback Form:</strong>{' '}
                <a href="/feedback" className="text-secondary hover:text-primary-dark underline">
                  Submit Feedback
                </a>
              </p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <a href="/privacy" className="hover:text-secondary underline">Privacy Policy</a>
              <span>|</span>
              <a href="/cookies" className="hover:text-secondary underline">Cookie Policy</a>
              <span>|</span>
              <a href="/" className="hover:text-secondary underline">Back to Home</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
